import { Dialog } from "./dialog";
import { Button } from "../buttons/button";
import { AddIcon, SubtractIcon } from "@/icons";
import { useEffect, useState } from "react";

interface ItemData {
  item_name: string;
  shop_price: number;
  description: string;
}

interface ShopBuyConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  item: ItemData | null;
  initialAmount: number;
  balance: number;
  isLoading?: boolean;
  onConfirm: (amount: number) => void;
}

export const ShopBuyConfirmDialog = ({
  isOpen,
  onClose,
  item,
  initialAmount,
  balance,
  isLoading,
  onConfirm,
}: ShopBuyConfirmDialogProps) => {
  const [amount, setAmount] = useState(initialAmount);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAmount(initialAmount);
      // Small delay to trigger entry animation
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
    }
  }, [isOpen, initialAmount]);

  if (!item) return null;

  const totalPrice = item.shop_price * amount;
  const canAfford = balance >= totalPrice;

  const parsedItemName = item.item_name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  // Determine max affordable amount
  // We don't strictly enforce it here to allow the user to see they can't afford it,
  // but we disable the buy button.

  const handleAmountChange = (delta: number) => {
    setAmount((prev) => {
      const newValue = prev + delta;
      return newValue < 1 ? 1 : newValue > 99 ? 99 : newValue;
    });
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div
        className={`relative w-[85vw] max-w-md mx-auto rounded-2xl bg-white shadow-xl overflow-hidden transition-all duration-300 ease-out transform ${
          isAnimating
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-4"
        }`}
      >
        {/* Header Background */}
        <div
          className="absolute inset-0 z-0 pointer-events-none rounded-2xl rotate-180"
          style={{
            backgroundImage: "url(/assets/app-ui/textre.png)",
            backgroundSize: "cover",
            backgroundPosition: "left",
            backgroundRepeat: "repeat",
            mixBlendMode: "initial",
            opacity: 0.03,
          }}
        />

        {/* Header Section */}
        <div
          className="bg-pk-black p-6 pb-24 flex flex-col items-start justify-center relative transition-colors duration-300"
          style={{
            clipPath: "polygon(0 0, 100% 0, 100% 85%, 0 100%)",
          }}
        >
          {/* Background Pattern Overlay */}
          <div className="absolute inset-0 opacity-10 bg-[url('/assets/app-ui/pattern-dots.png')] bg-repeat z-0" />

          <h1 className="text-white z-10 text-3xl font-bold font-[fisherman] tracking-tight">
            {parsedItemName}
          </h1>
          <div className="flex gap-4 items-center">
            <img
              src="/assets/app-ui/poke-coin.png"
              alt="Coins"
              className="w-auto h-10"
            />
            <h2 className="text-pk-yellow-3 z-10 text-4xl font-semibold tracking-wide mt-1">
              {item.shop_price}
            </h2>
          </div>
        </div>

        {/* Content Section */}
        <div className="relative z-20 px-6 pb-6 pt-0 text-center -mt-20 flex flex-col gap-6">
          {/* Item Image */}
          <div className="relative w-max mx-auto group">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-pk-blue-3/20 rounded-full blur-xl group-hover:bg-pk-blue-3/40 transition-all duration-500" />
            <img
              src={`/assets/items/${item.item_name}.png`}
              alt={item.item_name}
              className="relative z-20 w-32 h-32 object-contain drop-shadow-lg transform group-hover:scale-110 transition-transform duration-300"
            />
            {/* Shadow */}
            <div className="z-10 absolute translate-y-[45%] bottom-2 left-1/2 -translate-x-1/2 w-24 h-8 bg-black/20 rounded-[100%] blur-sm scale-x-100 group-hover:scale-x-75 transition-transform duration-300"></div>
          </div>

          {/* Description */}
          <div className="rounded-lg bg-gray-50 p-3 border border-gray-100 shadow-inner">
            <p className="text-gray-600 leading-tight font-light text-sm tracking-wide text-left">
              {item.description}
            </p>
          </div>

          {/* Quantity Controls */}
          <div className="bg-gray-100 rounded-xl p-2 flex items-center justify-between shadow-inner">
            <button
              onClick={() => handleAmountChange(-1)}
              disabled={amount <= 1 || isLoading}
              className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <SubtractIcon className="w-4 h-4 fill-gray-600" />
            </button>

            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold text-gray-800 leading-none">
                {amount}
              </span>
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">
                Quantity
              </span>
            </div>

            <button
              onClick={() => handleAmountChange(1)}
              disabled={amount >= 99 || isLoading}
              className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <AddIcon className="w-4 h-4 fill-gray-600" />
            </button>
          </div>

          {/* Total & Actions */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between px-2">
              <span className="text-gray-500 text-sm font-semibold uppercase tracking-wider">
                Total Cost
              </span>

              <div className="flex items-center gap-2">
                <img
                  src="/assets/app-ui/poke-coin.png"
                  alt="Coins"
                  className="w-auto h-6"
                />

                <span
                  className={`text-xl font-bold ${
                    canAfford ? "text-pk-gray-2" : "text-pk-red-4"
                  }`}
                >
                  {totalPrice}
                </span>
              </div>
            </div>

            <div className="w-full flex items-center gap-3">
              <Button
                variant="black"
                className="flex-1"
                onClick={onClose}
                disabled={isLoading || false}
              >
                <span>CANCEL</span>
              </Button>
              <Button
                variant="blue"
                className="flex-1 "
                disabled={!canAfford || isLoading || false}
                onClick={() => onConfirm(amount)}
              >
                <span className="py-3">
                  {isLoading ? "BUYING..." : "CONFIRM"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
